// src/hooks/useContentful.js
// Custom hook for fetching data from Contentful

import { useState, useEffect, useCallback, useRef } from 'react';
import { fetchContentful } from '../contentful/contentfulClient';

/**
 * Custom hook for fetching data from Contentful
 * Handles loading, error states, and caching
 *
 * @param {string} query - GraphQL query string
 * @param {object} variables - Query variables
 * @param {object} options - Hook options
 * @param {boolean} options.skip - Skip the query
 * @param {function} options.mapper - Data transformation function
 * @param {number} options.cacheTime - Cache duration in ms (default: 5 min)
 * @returns {object} { data, loading, error, refetch }
 */
export const useContentful = (query, variables = {}, options = {}) => {
  const { skip = false, mapper = null, cacheTime = 5 * 60 * 1000 } = options;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(!skip);
  const [error, setError] = useState(null);

  // Cache reference
  const cacheRef = useRef(new Map());
  const cacheKey = JSON.stringify({ query, variables });

  const fetchData = useCallback(async () => {
    // Check cache first
    const cached = cacheRef.current.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < cacheTime) {
      setData(cached.data);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const { data: responseData, error: responseError } = await fetchContentful(query, variables);

    if (responseError) {
      setError(responseError);
      setLoading(false);
      return;
    }

    // Apply mapper if provided
    const processedData = mapper ? mapper(responseData) : responseData;

    // Update cache
    cacheRef.current.set(cacheKey, {
      data: processedData,
      timestamp: Date.now(),
    });

    setData(processedData);
    setLoading(false);
  }, [query, variables, mapper, cacheKey, cacheTime]);

  useEffect(() => {
    if (skip) {
      setLoading(false);
      return;
    }

    fetchData();
  }, [fetchData, skip]);

  const refetch = useCallback(() => {
    // Clear cache for this query
    cacheRef.current.delete(cacheKey);
    return fetchData();
  }, [fetchData, cacheKey]);

  return { data, loading, error, refetch };
};

/**
 * Hook specifically for fetching resources list
 * Pre-configured with the resources query and mapper
 */
export const useResourcesList = () => {
  const { RESOURCES_LIST_QUERY } = require('../contentful/queries');
  const { mapResourcesList } = require('../contentful/mappers');

  return useContentful(RESOURCES_LIST_QUERY, {}, {
    mapper: mapResourcesList,
  });
};

/**
 * Hook for fetching a single article by slug
 */
export const useArticle = (slug) => {
  const { ARTICLE_BY_SLUG_QUERY } = require('../contentful/queries');
  const { mapArticle } = require('../contentful/mappers');

  return useContentful(
    ARTICLE_BY_SLUG_QUERY,
    { slug },
    {
      skip: !slug,
      mapper: (data) => {
        const article = data?.articleCollection?.items?.[0];
        return mapArticle(article);
      },
    }
  );
};

/**
 * Hook for fetching a single case study by slug
 */
export const useCaseStudy = (slug) => {
  const { CASE_STUDY_BY_SLUG_QUERY } = require('../contentful/queries');
  const { mapCaseStudy } = require('../contentful/mappers');

  return useContentful(
    CASE_STUDY_BY_SLUG_QUERY,
    { slug },
    {
      skip: !slug,
      mapper: (data) => {
        const caseStudy = data?.caseStudyCollection?.items?.[0];
        return mapCaseStudy(caseStudy);
      },
    }
  );
};

export default useContentful;
